import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { Unlink } from 'lucide-react';
import ScanQRHeader from '@/components/scan-qr/ScanQRHeader';
import Onboarding from '@/components/scan-qr/Onboarding';
import ProductDetailsForm from '@/components/scan-qr/ProductDetailsForm';
import ConfirmationPage, { type ProductData } from '@/components/scan-qr/ConfirmationPage';
import ActionPage from '@/components/scan-qr/ActionPage';
import ProtectionPlanPage from '@/components/scan-qr/ProtectionPlanPage';
import ContactVerification, { type ContactDetails } from '@/components/scan-qr/ContactVerification';

import { getMerchantProductCatalogue, type Product, type MerchantTerms } from '@/service/returnService';
import { loadQrDraft, pingQrActivity, clearQrDraft } from '@/utils/qrCache'; const ScanQRPage: React.FC = () => {
    const { merchantId } = useParams<{ merchantId: string }>();
    const navigate = useNavigate();
    const [merchant, setMerchant] = useState<{ name: string; logo_url?: string } | null>(null);
    const [merchantTerms, setMerchantTerms] = useState<MerchantTerms | undefined>(undefined);
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [productData, setProductData] = useState<ProductData | null>(null);
    const [contactDetails, setContactDetails] = useState<ContactDetails | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Initial cache load
    useEffect(() => {
        if (merchantId) {
            const cached = loadQrDraft(merchantId);
            if (cached) {
                setProductData(cached);
            } else {
                setProductData(null);
            }
        }
    }, [merchantId]);

    // Throttle user activity pings to avoid DOM lag
    useEffect(() => {
        if (!merchantId) return;

        let lastPing = Date.now();
        const handleActivity = () => {
            const now = Date.now();
            if (now - lastPing > 60000) { // Push timestamp at most once per minute
                lastPing = now;
                pingQrActivity(merchantId);
            }
        };

        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('keydown', handleActivity);
        window.addEventListener('scroll', handleActivity);
        window.addEventListener('click', handleActivity);
        window.addEventListener('touchstart', handleActivity);

        return () => {
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keydown', handleActivity);
            window.removeEventListener('scroll', handleActivity);
            window.removeEventListener('click', handleActivity);
            window.removeEventListener('touchstart', handleActivity);
        };
    }, [merchantId]);

    useEffect(() => {
        const fetchMerchantAndProducts = async () => {
            if (!merchantId) {
                setError("Invalid link");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            try {
                const response = await getMerchantProductCatalogue({ merchant_id: merchantId });
                console.log("Fetched products:", response);

                if (response.success && response.data.length > 0) {
                    setProducts(response.data);
                    // Extract merchant info from the first product
                    const firstProduct = response.data[0];
                    setMerchant({
                        name: firstProduct.merchants.name,
                        logo_url: firstProduct.merchants.logo_url,
                    });
                    setMerchantTerms(firstProduct.merchants.terms);
                } else {
                    console.error("No products found for this merchant");
                    setError("Link Expired or Invalid");
                }
            } catch (err) {
                console.error("Failed to fetch merchant products:", err);
                setError("Link Expired or Invalid");
            } finally {
                setLoading(false);
            }
        };

        fetchMerchantAndProducts();
    }, [merchantId]);

    const handleContinue = () => {
        navigate('details');
    };

    const handleClose = () => {
        navigate('/');
    };

    const handleConfirmProduct = (data: ProductData) => {
        // Store product data and navigate to confirmation
        setProductData(data);
        navigate('confirm');
    };

    const handleFinalConfirm = () => {
        console.log('Product confirmed', productData);
        navigate('actions');
    };

    const handleSelectAction = (actionId: string) => {
        if (actionId === 'accidental-damage' || actionId === 'extended-warranty') {
            navigate(actionId);
        /*
        } else if (actionId === 'return-product') {
            navigate('return-product');
        */
        } else {
            console.log('Action selected:', actionId);
        }
    };

    const handlePlanContinue = (selectedPlan?: { label: string; price: number }) => {
        if (selectedPlan) {
            console.log('Plan selected:', selectedPlan, productData);
            // TODO: Navigate to payment for plans
        } else {
            // For return product, navigate to contact verification
            navigate('return-contact');
        }
    };

    const handlePlanGoBack = () => {
        navigate('actions');
    };

    const handleContactComplete = (data: ContactDetails, code: string) => {
        console.log('Verification complete:', data, code);
        setContactDetails(data);
        if (merchantId) {
            clearQrDraft(merchantId);
        }
        // TODO: Complete return request and navigate to dashboard
        navigate('/customer-login');
    };

    const handleGoBackToEdit = () => {
        navigate('details');
    };

    if (loading) {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-50">
                <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-cyan-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-white sm:tw-bg-gray-50 sm:tw-p-4">
                <div className="tw-bg-white tw-w-full sm:tw-max-w-md sm:tw-rounded-2xl sm:tw-shadow-xl tw-overflow-hidden tw-relative tw-p-6 sm:tw-p-8 tw-min-h-screen sm:tw-min-h-0 tw-flex tw-flex-col sm:tw-block">
                    <ScanQRHeader
                        merchantName={merchant?.name}
                        merchantLogo={merchant?.logo_url}
                        onClose={handleClose}
                    />

                    <div className="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-gap-4 tw-pt-10">
                        <div className="tw-w-20 tw-h-20 tw-bg-red-50 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
                            <Unlink className="tw-w-10 tw-h-10 tw-text-red-500" strokeWidth={1.5} />
                        </div>
                        <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900">Link Expired or Invalid</h2>
                        <p className="tw-text-gray-500 tw-text-sm sm:tw-text-base tw-max-w-[280px]">
                            The link you scanned seems to be broken or the merchant details could not be found.
                        </p>

                        <button
                            onClick={handleClose}
                            className="tw-mt-8 tw-w-full tw-py-3 tw-bg-cyan-400 hover:tw-bg-cyan-500 tw-text-white tw-font-semibold tw-rounded-lg tw-transition-colors"
                        >
                            Return to Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!merchant) return null; // Should not happen with fallback

    return (
        <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-white sm:tw-bg-gray-50 sm:tw-p-4">
            <div className="tw-bg-white tw-w-full sm:tw-max-w-md sm:tw-rounded-2xl sm:tw-shadow-xl tw-overflow-hidden tw-relative tw-p-6 sm:tw-p-8 tw-min-h-screen sm:tw-min-h-0 tw-flex tw-flex-col sm:tw-block">

                <ScanQRHeader
                    merchantName={merchant?.name}
                    merchantLogo={merchant?.logo_url}
                    onClose={handleClose}
                />

                <Routes>
                    <Route index element={<Onboarding merchant={merchant} onContinue={handleContinue} />} />
                    <Route path="details" element={
                        <ProductDetailsForm
                            onClose={handleClose}
                            onConfirm={handleConfirmProduct}
                            initialData={productData}
                            products={products}
                            merchantName={merchant.name}
                            merchantId={merchantId}
                        />
                    } />
                    <Route path="confirm" element={
                        productData ? (
                            <ConfirmationPage
                                productData={productData}
                                onConfirm={handleFinalConfirm}
                                onGoBack={handleGoBackToEdit}
                                onEdit={handleGoBackToEdit}
                            />
                        ) : (
                            <Navigate to={`/customer/scan-qr/${merchantId}`} replace />
                        )
                    } />
                    <Route path="actions" element={
                        productData ? (
                            <ActionPage
                                productData={productData}
                                merchantTerms={merchantTerms}
                                onSelectAction={handleSelectAction}
                            />
                        ) : (
                            <Navigate to={`/customer/scan-qr/${merchantId}`} replace />
                        )
                    } />
                    <Route path="accidental-damage" element={
                        productData ? (
                            <ProtectionPlanPage
                                variant="accidental-damage"
                                productData={productData}
                                merchantTerms={merchantTerms}
                                onContinue={handlePlanContinue}
                                onGoBack={handlePlanGoBack}
                            />
                        ) : (
                            <Navigate to={`/customer/scan-qr/${merchantId}`} replace />
                        )
                    } />
                    <Route path="extended-warranty" element={
                        productData ? (
                            <ProtectionPlanPage
                                variant="extended-warranty"
                                productData={productData}
                                merchantTerms={merchantTerms}
                                onContinue={handlePlanContinue}
                                onGoBack={handlePlanGoBack}
                            />
                        ) : (
                            <Navigate to={`/customer/scan-qr/${merchantId}`} replace />
                        )
                    } />
                    {/*
                    <Route path="return-product" element={
                        productData ? (
                            <ProtectionPlanPage
                                variant="return-product"
                                productData={productData}
                                merchantTerms={merchantTerms}
                                onContinue={handlePlanContinue}
                                onGoBack={handlePlanGoBack}
                            />
                        ) : (
                            <Navigate to={`/customer/scan-qr/${merchantId}`} replace />
                        )
                    } />
                    */}
                    {/* <Route path="return-contact" element={
                        productData && merchantId ? (
                            <ContactVerification
                                productId={productData.id}
                                merchantId={merchantId}
                                initialData={contactDetails}
                                onComplete={handleContactComplete}
                            />
                        ) : (
                            <Navigate to={`/customer/scan-qr/${merchantId}`} replace />
                        )
                    } /> */}
                </Routes>

            </div>
        </div>
    );
};

export default ScanQRPage;
